import React from "react";
import { Box, Button, Card, Typography } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import downloadIcon from "../../../../../assets/Download.svg";
import { dateFormat, download } from "../../../../../constants/Constants";
import moment from "moment";
import { DocumentStyles } from "./DocumentStyles";
import { IDocument } from "../../../../../models/user/policyDetails/Document";
import { useTranslation } from "react-i18next";

interface Props {
  documentData: IDocument;
  handleFileClick: (type: string, documentData: IDocument) => void;
  view: boolean;
}

const Document: React.FC<Props> = ({ documentData, handleFileClick, view }) => {
  const { t } = useTranslation();

  return (
    <Box 
      sx={DocumentStyles.cardContainer}
      id="document_container"
      data-test-id="document_container"
      aria-label="Document container"
    >
      <Card 
        sx={DocumentStyles.card}
        id="document_card"
        data-test-id="document_card"
        aria-label="Document card"
      >
        <Box 
          sx={DocumentStyles.cardContentBox}
          id="document_content"
          data-test-id="document_content"
          aria-label="Document content"
        >
          <Box 
            sx={DocumentStyles.imageContainer}
            id="document_pdf_icon_container"
            data-test-id="document_pdf_icon_container"
            aria-label="PDF icon container"
          >
            <PictureAsPdfIcon 
              sx={DocumentStyles.pdfIcon}
              id="document_pdf_icon"
              data-test-id="document_pdf_icon"
              aria-label="PDF icon"
            />
          </Box>
          <Box 
            sx={DocumentStyles.cardItem}
            id="document_details"
            data-test-id="document_details"
            aria-label="Document details"
          >
            <Typography 
              sx={DocumentStyles.cardItemHeadingStyle}
              id="document_description"
              data-test-id="document_description"
              aria-label={`Document description: ${documentData.description}`}
            >
              {documentData.description}
            </Typography>
            <Typography 
              sx={DocumentStyles.cardItemContentStyle}
              id="document_date"
              data-test-id="document_date"
              aria-label={`Document date: ${moment(documentData.addDate).format(dateFormat)}`}
            >
              {t("Date")}: {moment(documentData.addDate).format(dateFormat)}
            </Typography>
          </Box>
        </Box>
        <Box 
          sx={DocumentStyles.buttonGroup} 
          aria-label="Document actions"
          id="document_actions"
          data-test-id="document_actions"
        >
          {view ? (
            <Button
              id="view_document_button"
              data-test-id="view_document_button"
              aria-label="View document"
              sx={{
                gap: "4px",
                padding: "0.5rem",
                boxShadow: "none",
                ":hover": {
                  backgroundColor: "white",
                },
                ":disabled": {
                  backgroundColor: "white !important",
                },
              }}
              onClick={() => handleFileClick("view", documentData)}
              // disabled
            >
              <VisibilityIcon
                titleAccess={t("View Document")}
                sx={{ color: "black" }}
                id="view_document_icon"
                data-test-id="view_document_icon"
                aria-label="View document icon"
              />
              <Typography 
                sx={DocumentStyles.text}
                id="view_document_text"
                data-test-id="view_document_text"
                aria-label="View document text"
              >
                {t("View")}
              </Typography>
            </Button>
          ) : (
            <></>
          )}
          <Button
            id="download_document_button"
            data-test-id="download_document_button"
            sx={DocumentStyles.downloadButton}
            aria-label="Download document"
            onClick={() => handleFileClick("download", documentData)}
            // disabled
          >
            <img 
              src={downloadIcon} 
              alt="download Icon"
              id="download_document_icon"
              data-test-id="download_document_icon"
              aria-label="Download document icon"
            />
            <Typography 
              sx={DocumentStyles.text}
              id="download_document_text"
              data-test-id="download_document_text"
              aria-label="Download document text"
            >
              {t(download)}
            </Typography>
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default Document;
